// src/features/favorites/favoritesSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_URL = 'https://mwl26uv0m7.execute-api.us-east-1.amazonaws.com/prod';


// 1) fetchFavorites: now takes { token }
export const fetchFavorites = createAsyncThunk(
  'favorites/fetchFavorites',
  async ({ token }, thunkAPI) => {
    try {
      const res = await fetch(`${API_URL}/favorites`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`GET /favorites failed (${res.status})`);
      }
      const data = await res.json();
      localStorage.setItem('favorites', JSON.stringify(data || {}));
      return data || {};
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 2) saveList: now takes { token, listName, items }
export const saveList = createAsyncThunk(
  'favorites/saveList',
  async ({ token, listName, items }, thunkAPI) => {
    const encoded = encodeURIComponent(listName)
    try {
      const res = await fetch(`${API_URL}/favorites/${encoded}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        }
      );
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`PUT /favorites/${listName} failed (${res.status})`);
      }
      const current = JSON.parse(localStorage.getItem('favorites') || '{}');
      current[listName] = items;
      localStorage.setItem('favorites', JSON.stringify(current));
      return { listName, items };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 3) deleteList: now takes { token, listName }
export const deleteList = createAsyncThunk(
  'favorites/deleteList',
  async ({ token, listName }, thunkAPI) => {
    const encoded = encodeURIComponent(listName)
    try {
      const res = await fetch(
        `${API_URL}/favorites/${encoded}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`DELETE /favorites/${listName} failed (${res.status})`);
      }
      const current = JSON.parse(localStorage.getItem('favorites') || '{}');
      delete current[listName];
      localStorage.setItem('favorites', JSON.stringify(current));
      return listName;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 4) fetchColumnPresets: fetch all column presets
export const fetchColumnPresets = createAsyncThunk(
  'favorites/fetchColumnPresets',
  async ({ token }, thunkAPI) => {
    try {
      const res = await fetch(`${API_URL}/favorites/column-presets`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`GET /favorites/column-presets failed (${res.status})`);
      }
      const data = await res.json();
      localStorage.setItem('columnPresets', JSON.stringify(data || {}));
      return data || {};
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 5) saveColumnPreset: save/update a column preset
export const saveColumnPreset = createAsyncThunk(
  'favorites/saveColumnPreset',
  async ({ token, presetName, columns }, thunkAPI) => {
    const encoded = encodeURIComponent(presetName);
    try {
      const res = await fetch(`${API_URL}/favorites/column-presets/${encoded}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ columns }),
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`PUT /favorites/column-presets/${presetName} failed (${res.status})`);
      }
      const current = JSON.parse(localStorage.getItem('columnPresets') || '{}');
      current[presetName] = columns;
      localStorage.setItem('columnPresets', JSON.stringify(current));
      return { presetName, columns };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 6) deleteColumnPreset: delete a column preset
export const deleteColumnPreset = createAsyncThunk(
  'favorites/deleteColumnPreset',
  async ({ token, presetName }, thunkAPI) => {
    const encoded = encodeURIComponent(presetName);
    try {
      const res = await fetch(`${API_URL}/favorites/column-presets/${encoded}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`DELETE /favorites/column-presets/${presetName} failed (${res.status})`);
      }
      const current = JSON.parse(localStorage.getItem('columnPresets') || '{}');
      delete current[presetName];
      localStorage.setItem('columnPresets', JSON.stringify(current));
      return presetName;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 7) fetchMetricPresets: fetch all metric presets
export const fetchMetricPresets = createAsyncThunk(
  'favorites/fetchMetricPresets',
  async ({ token }, thunkAPI) => {
    try {
      const res = await fetch(`${API_URL}/favorites/metric-presets`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`GET /favorites/metric-presets failed (${res.status})`);
      }
      const data = await res.json();
      localStorage.setItem('metricPresets', JSON.stringify(data || {}));
      return data || {};
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 8) saveMetricPreset: save/update a metric preset
export const saveMetricPreset = createAsyncThunk(
  'favorites/saveMetricPreset',
  async ({ token, presetName, metrics }, thunkAPI) => {
    const encoded = encodeURIComponent(presetName);
    try {
      const res = await fetch(`${API_URL}/favorites/metric-presets/${encoded}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ metrics }),
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`PUT /favorites/metric-presets/${presetName} failed (${res.status})`);
      }
      const current = JSON.parse(localStorage.getItem('metricPresets') || '{}');
      current[presetName] = metrics;
      localStorage.setItem('metricPresets', JSON.stringify(current));
      return { presetName, metrics };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// 9) deleteMetricPreset: delete a metric preset
export const deleteMetricPreset = createAsyncThunk(
  'favorites/deleteMetricPreset',
  async ({ token, presetName }, thunkAPI) => {
    const encoded = encodeURIComponent(presetName);
    try {
      const res = await fetch(`${API_URL}/favorites/metric-presets/${encoded}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error(`DELETE /favorites/metric-presets/${presetName} failed (${res.status})`);
      }
      const current = JSON.parse(localStorage.getItem('metricPresets') || '{}');
      delete current[presetName];
      localStorage.setItem('metricPresets', JSON.stringify(current));
      return presetName;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// initial state from localStorage
const saved = localStorage.getItem('favorites');
const initialLists = saved ? JSON.parse(saved) : {};
const savedColumnPresets = localStorage.getItem('columnPresets');
const initialColumnPresets = savedColumnPresets ? JSON.parse(savedColumnPresets) : {};
const savedMetricPresets = localStorage.getItem('metricPresets');
const initialMetricPresets = savedMetricPresets ? JSON.parse(savedMetricPresets) : {};

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: {
    lists: initialLists,
    columnPresets: initialColumnPresets,
    metricPresets: initialMetricPresets,
    status: saved ? 'succeeded' : 'idle',
    columnPresetsStatus: savedColumnPresets ? 'succeeded' : 'idle',
    metricPresetsStatus: savedMetricPresets ? 'succeeded' : 'idle',
    error: null,
  },
  reducers: {
    clearFavorites(state) {
      state.lists = {};
      state.status = 'idle';
      state.error = null;
      localStorage.removeItem('favorites');
    },
    clearColumnPresets(state) {
      state.columnPresets = {};
      state.columnPresetsStatus = 'idle';
      localStorage.removeItem('columnPresets');
    },
    clearMetricPresets(state) {
      state.metricPresets = {};
      state.metricPresetsStatus = 'idle';
      localStorage.removeItem('metricPresets');
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFavorites
      .addCase(fetchFavorites.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.lists = action.payload;
        state.error = null;
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // saveList
      .addCase(saveList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { listName, items } = action.payload;
        state.lists[listName] = items;
        state.error = null;
      })
      .addCase(saveList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // deleteList
      .addCase(deleteList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteList.fulfilled, (state, action) => {
        state.status = 'succeeded';
        delete state.lists[action.payload];
        state.error = null;
      })
      .addCase(deleteList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // fetchColumnPresets
      .addCase(fetchColumnPresets.pending, (state) => {
        state.columnPresetsStatus = 'loading';
      })
      .addCase(fetchColumnPresets.fulfilled, (state, action) => {
        state.columnPresetsStatus = 'succeeded';
        state.columnPresets = action.payload;
        state.error = null;
      })
      .addCase(fetchColumnPresets.rejected, (state, action) => {
        state.columnPresetsStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      // saveColumnPreset
      .addCase(saveColumnPreset.pending, (state) => {
        state.columnPresetsStatus = 'loading';
      })
      .addCase(saveColumnPreset.fulfilled, (state, action) => {
        state.columnPresetsStatus = 'succeeded';
        const { presetName, columns } = action.payload;
        state.columnPresets[presetName] = columns;
        state.error = null;
      })
      .addCase(saveColumnPreset.rejected, (state, action) => {
        state.columnPresetsStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      // deleteColumnPreset
      .addCase(deleteColumnPreset.pending, (state) => {
        state.columnPresetsStatus = 'loading';
      })
      .addCase(deleteColumnPreset.fulfilled, (state, action) => {
        state.columnPresetsStatus = 'succeeded';
        delete state.columnPresets[action.payload];
        state.error = null;
      })
      .addCase(deleteColumnPreset.rejected, (state, action) => {
        state.columnPresetsStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      // fetchMetricPresets
      .addCase(fetchMetricPresets.pending, (state) => {
        state.metricPresetsStatus = 'loading';
      })
      .addCase(fetchMetricPresets.fulfilled, (state, action) => {
        state.metricPresetsStatus = 'succeeded';
        state.metricPresets = action.payload;
        state.error = null;
      })
      .addCase(fetchMetricPresets.rejected, (state, action) => {
        state.metricPresetsStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      // saveMetricPreset
      .addCase(saveMetricPreset.pending, (state) => {
        state.metricPresetsStatus = 'loading';
      })
      .addCase(saveMetricPreset.fulfilled, (state, action) => {
        state.metricPresetsStatus = 'succeeded';
        const { presetName, metrics } = action.payload;
        state.metricPresets[presetName] = metrics;
        state.error = null;
      })
      .addCase(saveMetricPreset.rejected, (state, action) => {
        state.metricPresetsStatus = 'failed';
        state.error = action.payload || action.error.message;
      })

      // deleteMetricPreset
      .addCase(deleteMetricPreset.pending, (state) => {
        state.metricPresetsStatus = 'loading';
      })
      .addCase(deleteMetricPreset.fulfilled, (state, action) => {
        state.metricPresetsStatus = 'succeeded';
        delete state.metricPresets[action.payload];
        state.error = null;
      })
      .addCase(deleteMetricPreset.rejected, (state, action) => {
        state.metricPresetsStatus = 'failed';
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearFavorites, clearColumnPresets, clearMetricPresets } = favoritesSlice.actions;
export default favoritesSlice.reducer;
